import {
    MessageActionProps,
    MessageActionTypes,
} from '../types'

import {DateTime} from 'luxon'
import {TFunction} from 'i18next'
import {removeMember} from 'api/action/removeMember'
import {toast} from 'components/Toast'
import {updateMember} from 'api/action/updateMember'
import {updateMessage} from 'api/action/updateMessage'
import {updateUserMessages} from 'api/action/updateUserMessages'

export const mapMessageActionToModalTitleText = (action: MessageActionTypes, t: TFunction) => {
    let text = ''
    let title = ''

    switch (action) {
        case MessageActionTypes.silence24h:
            text = t('Are you sure you want to silence this user for {{count}} hour?', {count: 24})
            title = t('Silence user')
            break
        case MessageActionTypes.silence1h:
            text = t('Are you sure you want to silence this user for {{count}} hour?', {count: 1})
            title = t('Silence user')
            break
        case MessageActionTypes.silence15m:
            text = t('Are you sure you want to silence this user for {{minutes}} minutes?', {minutes: 15})
            title = t('Silence user')
            break
        case MessageActionTypes.silence5m:
            text = t('Are you sure you want to silence this user for {{minutes}} minutes?', {minutes: 5})
            title = t('Silence user')
            break
        case MessageActionTypes.silence:
            text = t('Are you sure you want to silence this user?')
            title = t('Silence user')
            break
        case MessageActionTypes.unsilence:
            text = t('Are you sure you want to unsilence this user?')
            title = t('Unsilence user')
            break
        case MessageActionTypes.removeUser:
            text = t('Are you sure you want to remove this user?')
            title = t('Remove user')
            break
        case MessageActionTypes.showNewMessages:
            text = t('Are you sure you want to show all new messages from this user?')
            title = t('Show all new messages')
            break
        case MessageActionTypes.hideNewMessages:
            text = t('Are you sure you want to hide all new messages from this user?')
            title = t('Hide all new messages')
            break
        case MessageActionTypes.hideAllMessages:
            text = t('Are you sure you want to hide all messages from this user?')
            title = t('Hide all messages')
            break
        case MessageActionTypes.hideMessage:
        default:
            text = t('Are you sure you want to hide this message?')
            title = t('Hide message')
    }

    return {
        text,
        title,
    }
}

// eslint-disable-next-line complexity
export const messageAction = (props: MessageActionProps) => {
    const {
        action, message, dispatch, t, channelId,
    } = props

    if ([MessageActionTypes.silence,
        MessageActionTypes.silence5m,
        MessageActionTypes.silence15m,
        MessageActionTypes.silence1h,
        MessageActionTypes.silence24h,
        MessageActionTypes.unsilence].includes(action)) {
        if (message.message_user_id) {
            let minutesToAdd = 0

            if (action === MessageActionTypes.silence5m) {
                minutesToAdd = 5
            } else if (action === MessageActionTypes.silence15m) {
                minutesToAdd = 15
            } else if (action === MessageActionTypes.silence1h) {
                minutesToAdd = 60
            } else if (action === MessageActionTypes.silence24h) {
                minutesToAdd = 60 * 24
            }

            dispatch(updateMember({
                channel_id: channelId,
                ...minutesToAdd ?
                    {
                        interval_end: DateTime.now().plus({minutes: minutesToAdd})
                            .toSeconds(),
                    }
                    : {},
                member_attrs: {silenced: action !== MessageActionTypes.unsilence},
                user_id: message.message_user_id,

            }))
                .unwrap()
                .then(() => {
                    if (action === MessageActionTypes.unsilence) {
                        toast.success(t('Member unsilenced'))
                    } else {
                        toast.success(t('Member silenced'))
                    }
                }, () => {
                    if (action === MessageActionTypes.unsilence) {
                        toast.error(t('Error unsilencing member'))
                    } else {
                        toast.error(t('Error silencing member'))
                    }
                })
        }
    }

    if (action === MessageActionTypes.removeUser) {
        if (message.message_user_id) {
            dispatch(removeMember(
                {
                    channel_id: channelId,
                    user_id: message.message_user_id,
                },
            )).unwrap()
                .then(() => {
                    toast.success(t('Member removed'))
                }, () => {
                    toast.error(t('Error removing member'))
                })
        }
    }

    if ((action === MessageActionTypes.hideMessage || action === MessageActionTypes.showMessage) && channelId && message.message_id) {
        dispatch(updateMessage(
            {
                channel_id: channelId,
                message_hidden: action === MessageActionTypes.hideMessage,
                message_id: message.message_id,
            },
        )).unwrap()
            .then(() => {
                if (action === MessageActionTypes.hideMessage) {
                    toast.success(t('Message hidden'))
                } else {
                    toast.success(t('Message visible'))
                }
            }, () => {
                if (action === MessageActionTypes.hideMessage) {
                    toast.error(t('Error hiding message'))
                } else {
                    toast.error(t('Error showing message'))
                }
            })
    }

    if (action === MessageActionTypes.hideAllMessages && channelId && message.message_user_id) {
        dispatch(updateUserMessages({
            channel_id: channelId,
            interval_end: DateTime.now().toSeconds(),
            message_hidden: true,
            message_user_id: message.message_user_id,
        })).unwrap()
            .then(() => {
                toast.success(t('All user messages hidden'))
            }, () => {
                toast.error(t('Error hiding user messages'))
            })
    }

    if ((action === MessageActionTypes.hideNewMessages || action === MessageActionTypes.showNewMessages) &&
        channelId && message.message_user_id) {
        dispatch(updateMember({
            channel_id: channelId,
            member_attrs: {autohide: action === MessageActionTypes.hideNewMessages},
            user_id: message.message_user_id,
        }))
            .unwrap()
            .then(() => {
                if (action === MessageActionTypes.hideNewMessages) {
                    toast.success(t('All user new messages will be hidden'))
                } else {
                    toast.success(t('All user new messages will be visible'))
                }
            }, () => {
                if (action === MessageActionTypes.hideNewMessages) {
                    toast.error(t('Error hiding user future messages'))
                } else {
                    toast.error(t('Error showing user future messages'))
                }
            })
    }
}
